import React, {
  useMemo,
  useCallback,
  useState,
  useRef,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import { find, get, isEmpty, last } from 'lodash';
import useGetLevels from './hooks/useGetLevels';
import DropdownSelect from './DropdownSelect';
import { getParentLevel, getRootLevel } from './utils';

export default function DrilldownQuestion({ question }) {
  const levels = get(
    question,
    'matrix.validation.drillDownConfiguration.config.levels',
    [],
  );
  const columns = get(question, 'matrix.columns', []);
  const columnLabels = columns.map(({ beforeLabel }) => beforeLabel);
  const flatLevels = useGetLevels(levels);
  const drilldownRef = useRef();
  const [selectedValues, setSelectedValues] = useState([]);
  const [currentLevels, setCurrentLevels] = useState(getRootLevel(levels));

  const onSelect = useCallback(
    (index) => (e) => {
      const newValues = [...selectedValues];
      if (newValues[index] === e.refId) {
        // its important to go two levels up in order to find the proper parents children!
        setSelectedValues(newValues.slice(0, index));
        setCurrentLevels(
          getParentLevel({ levels, flatLevels, refId: e.refId, steps: 1 }),
        );
      } else {
        newValues[index] = e.refId;
        // If we re-select the root level then we need to clear the next options
        setSelectedValues(newValues.slice(0, index + 1));
        setCurrentLevels(flatLevels[e.refId]);
      }
    },
    [selectedValues, flatLevels, levels],
  );

  const { hasFinished, selectedAnswer } = useMemo(() => {
    const lastSelectedValue = last(selectedValues);
    if (!lastSelectedValue) {
      return { hasFinished: false };
    }
    const lastLevel = flatLevels[lastSelectedValue];
    if (!lastLevel) {
      return { hasFinished: false };
    }

    const selectedQuestionAnswer = find(question.answers, [
      'refId',
      lastLevel.answerRefId,
    ]);
    return {
      hasFinished: isEmpty(lastLevel.children),
      selectedAnswer: selectedQuestionAnswer,
    };
  }, [selectedValues, flatLevels, question.answers]);

  useEffect(() => {
    Pollfish.ui.validateQuestionAnswers(
      Pollfish.ui.getClosestQuestionWrapperElement(drilldownRef?.current),
    );
  }, [hasFinished]);

  return (
    <div
      ref={drilldownRef}
      className={hasFinished ? 'answerClick' : ''}
      id={hasFinished ? selectedAnswer.id : ''}
    >
      {columnLabels.map((label, index) => {
        const selectedValue = selectedValues[index];
        const parentSelectedValue = selectedValues[index - 1];
        let options = [];
        if (selectedValue) {
          const level = flatLevels[selectedValue];
          let parentLevel = flatLevels[level.parentRefId];
          if (!level.parentRefId) {
            parentLevel = getRootLevel(levels);
          }
          if (!isEmpty(parentLevel.children)) {
            options = parentLevel.children.map((option) => ({
              value: option.value,
              label: option.value,
              refId: option.refId,
              answerId: option.answerId,
              isSelected: option.refId === level.refId,
            }));
          }
        } else if (parentSelectedValue || index === 0) {
          options = currentLevels.children.map((option) => ({
            value: option.value,
            label: option.value,
            refId: option.refId,
            answerId: option.answerId,
            parentRefId: currentLevels.refId,
          }));
        }

        return (
          <DropdownSelect
            key={label}
            label={label}
            options={options}
            onSelect={onSelect(index)}
            disabled={isEmpty(options)}
          />
        );
      })}
    </div>
  );
}

DrilldownQuestion.propTypes = {
  question: PropTypes.object,
};
